'use client';

import { Html } from '@react-three/drei';
import { formatMeasurementMm } from 'lib/measurements/formatMeasurement';
import { memo } from 'react';
import * as THREE from 'three';

interface MeasurementLineProps {
  points: THREE.Vector3[];
  hovering: boolean;
}

function MeasurementLineComponent({ points, hovering }: MeasurementLineProps) {
  if (points.length === 0) return null;

  const start = points[0];
  const end = points[1] || points[0];
  const distance = start.distanceTo(end);

  // Calculate the midpoint for the label
  const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

  // Calculate direction vector and rotation
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();

  // Only proceed if we have a valid length
  if (length === 0) return null;

  // Create a quaternion for rotation
  const quaternion = new THREE.Quaternion();
  // Default up vector
  const up = new THREE.Vector3(0, 1, 0);

  // Normalize the direction vector
  direction.normalize();

  // Handle the case where direction is parallel to up vector
  if (Math.abs(Math.abs(direction.dot(up)) - 1) < 0.001) {
    // If parallel, rotate around X axis instead
    quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), direction.y > 0 ? 0 : Math.PI);
  } else {
    // Calculate rotation from up vector to direction
    const axis = new THREE.Vector3().crossVectors(up, direction);
    const angle = Math.acos(up.dot(direction));
    quaternion.setFromAxisAngle(axis, angle);
  }

  return (
    <group renderOrder={2}>
      {/* Line */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array([start.x, start.y, start.z, end.x, end.y, end.z])}
            itemSize={3}
            args={[new Float32Array([start.x, start.y, start.z, end.x, end.y, end.z]), 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color={hovering ? '#ffdd00' : '#ffff00'}
          linewidth={2}
          transparent
          opacity={hovering ? 0.9 : 0.6}
          depthTest={false}
        />
      </line>

      {/* Endpoint markers */}
      <mesh position={start} renderOrder={2}>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshBasicMaterial
          color={hovering ? '#ffdd00' : '#ffff00'}
          transparent
          opacity={hovering ? 0.9 : 0.6}
          depthTest={false}
        />
      </mesh>
      <mesh position={end} renderOrder={2}>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshBasicMaterial
          color={hovering ? '#ffdd00' : '#ffff00'}
          transparent
          opacity={hovering ? 0.9 : 0.6}
          depthTest={false}
        />
      </mesh>

      {/* Distance label */}
      <Html position={midpoint} center>
        <div
          style={{
            background: hovering ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.7)',
            color: hovering ? '#ffdd00' : 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            pointerEvents: 'none',
            transition: 'all 0.2s ease-in-out',
            whiteSpace: 'nowrap',
          }}
        >
          {formatMeasurementMm(distance)}
        </div>
      </Html>
    </group>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(MeasurementLineComponent);
